// ============================================================================
// TEAM MANAGEMENT PAGE
// Main page for viewing and managing teams
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Search, Archive } from 'lucide-react';
import { useTeams } from '../hooks/useTeams';
import { useAuth } from '../contexts/AuthContext';

export const TeamManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: teams, isLoading } = useTeams({ status: 'active', includeCounts: true });
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const filteredTeams = teams?.filter(team => {
    const matchesSearch = team.teamName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArchived = showArchived ? team.status === 'archived' : team.status === 'active';
    return matchesSearch && matchesArchived;
  });

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '700', margin: '0 0 8px 0' }}>
            Team Management
          </h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Manage teams and their members for task assignment
          </p>
        </div>

        {user?.role === 'system_admin' && (
          <button
            onClick={() => navigate('/teams/new')}
            style={{
              padding: '12px 20px',
              background: '#00c6c2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Create Team
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Search */}
        <div style={{ flex: 1, position: 'relative' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8'
            }}
          />
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        </div>

        {/* Show Archived Toggle */}
        <button
          onClick={() => setShowArchived(!showArchived)}
          style={{
            padding: '10px 16px',
            background: showArchived ? '#f1f5f9' : 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: showArchived ? '#475569' : '#64748b'
          }}
        >
          <Archive size={16} />
          {showArchived ? 'Hide' : 'Show'} Archived
        </button>
      </div>

      {/* Teams Grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '64px' }}>
          <div className="spinner" />
          <p style={{ color: '#64748b', marginTop: '16px' }}>Loading teams...</p>
        </div>
      ) : filteredTeams?.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px',
          background: '#f8fafc',
          borderRadius: '12px',
          border: '2px dashed #e2e8f0'
        }}>
          <Users size={48} color="#cbd5e1" />
          <h3 style={{ margin: '16px 0 8px 0', color: '#475569' }}>No teams found</h3>
          <p style={{ color: '#64748b', margin: 0 }}>
            {searchQuery
              ? 'Try a different search term'
              : 'Create your first team to get started'}
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '20px'
        }}>
          {filteredTeams?.map(team => (
            <TeamCard
              key={team.teamId}
              team={team}
              onClick={() => navigate(`/teams/${team.teamId}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Team Card Component
interface TeamCardProps {
  team: any;
  onClick: () => void;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '20px',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Team Header */}
      <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: '#00c6c2',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Users size={20} color="white" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            margin: '0 0 4px 0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {team.teamName}
          </h3>
          {team.description && (
            <p style={{
              fontSize: '13px',
              color: '#64748b',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {team.description}
            </p>
          )}
        </div>
      </div>

      {/* Team Stats */}
      <div style={{
        display: 'flex',
        gap: '12px',
        paddingTop: '12px',
        borderTop: '1px solid #f1f5f9'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
            {team.memberCount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Members</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
            {team.activeTaskCount || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Active Tasks</div>
        </div>
      </div>

      {/* Status Badge */}
      {team.status === 'archived' && (
        <div style={{
          marginTop: '12px',
          padding: '4px 8px',
          background: '#fef2f2',
          color: '#991b1b',
          borderRadius: '4px',
          fontSize: '11px',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          ARCHIVED
        </div>
      )}
    </div>
  );
};
